import React, { useEffect, useState } from 'react';
import Shepherd from 'shepherd.js';
import 'shepherd.js/dist/css/shepherd.css';

const ProductTour = ({ userPlan = 'starter', onTourComplete, currentSection, onNavigateToSection }) => {
  const [tour, setTour] = useState(null);
  const [isTourActive, setIsTourActive] = useState(false);

  useEffect(() => {
    // Initialize Shepherd tour with modern styling and smooth animations
    const shepherdTour = new Shepherd.Tour({
      defaultStepOptions: {
        cancelIcon: {
          enabled: true,
          label: 'Close tour'
        },
        classes: 'devello-tour-step',
        scrollTo: {
          behavior: 'smooth',
          block: 'center'
        },
        highlightClass: 'devello-tour-highlight',
        modalOverlayOpeningPadding: 12,
        popperOptions: {
          modifiers: [
            {
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                padding: 20
              }
            }
          ]
        }
      },
      useModalOverlay: true,
      keyboardNavigation: true
    });

    setTour(shepherdTour);

    return () => {
      if (shepherdTour && typeof shepherdTour.destroy === 'function') {
        shepherdTour.destroy();
      }
    };
  }, []);

  const startTour = () => {
    if (!tour) return;

    // Clear any existing steps
    tour.steps = [];

    // Welcome step - centered on dashboard
    tour.addStep({
      id: 'welcome',
      title: 'Welcome to DevelloTech! 🚀',
      text: `
        <div class="tour-content">
          <div class="tour-icon">🎯</div>
          <h3 class="tour-title">Let's get you set up in 5 minutes</h3>
          <p class="tour-description">I'll guide you through the essential setup to get your construction business running smoothly.</p>
          <div class="tour-progress">
            <div class="progress-bar">
              <div class="progress-fill" style="width: 0%"></div>
            </div>
            <span class="progress-text">Step 1 of ${userPlan === 'pro' ? '8' : '6'}</span>
          </div>
        </div>
      `,
      buttons: [
        {
          text: 'Skip tour',
          action: () => {
            tour.complete();
            setIsTourActive(false);
            if (onTourComplete) onTourComplete();
          },
          classes: 'btn-skip'
        },
        {
          text: 'Start setup',
          action: () => {
            // Navigate to settings first
            if (onNavigateToSection) onNavigateToSection('settings');
            // Small delay to ensure navigation completes
            setTimeout(() => tour.next(), 500);
          },
          classes: 'btn-primary'
        }
      ],
      attachTo: {
        element: '.dashboard-container',
        on: 'center'
      }
    });

    // Settings step - attach to sidebar settings button
    tour.addStep({
      id: 'settings',
      title: 'Complete Your Profile',
      text: `
        <div class="tour-content">
          <div class="tour-icon">⚙️</div>
          <h3 class="tour-title">Set up your company information</h3>
          <p class="tour-description">This information will appear on your invoices and client documents, making you look professional.</p>
          <div class="tour-features">
            <div class="feature-item">✓ Company name & contact details</div>
            <div class="feature-item">✓ Business address & information</div>
            <div class="feature-item">✓ Email notification preferences</div>
          </div>
          <div class="tour-progress">
            <div class="progress-bar">
              <div class="progress-fill" style="width: 20%"></div>
            </div>
            <span class="progress-text">Step 2 of ${userPlan === 'pro' ? '10' : '6'}</span>
          </div>
        </div>
      `,
      buttons: [
        {
          text: 'Previous',
          action: () => tour.back(),
          classes: 'btn-secondary'
        },
        {
          text: 'Next',
          action: () => {
            if (onNavigateToSection) onNavigateToSection('teams');
            setTimeout(() => tour.next(), 500);
          },
          classes: 'btn-primary'
        }
      ],
      attachTo: {
        element: 'a[data-section="settings"]',
        on: 'bottom'
      }
    });

    // Team management step
    tour.addStep({
      id: 'team',
      title: 'Add Your Team',
      text: `
        <div class="tour-content">
          <div class="tour-icon">👷‍♂️</div>
          <h3 class="tour-title">Build your team database</h3>
          <p class="tour-description">Add your team members and their hourly rates. This will be used in invoices and labour on projects.</p>
          <div class="tour-features">
            <div class="feature-item">✓ Team member names & contact info</div>
            <div class="feature-item">✓ Hourly rates for labour calculations</div>
            <div class="feature-item">✓ Roles and responsibilities</div>
          </div>
          <div class="tour-progress">
            <div class="progress-bar">
              <div class="progress-fill" style="width: 40%"></div>
            </div>
            <span class="progress-text">Step 3 of ${userPlan === 'pro' ? '8' : '6'}</span>
          </div>
        </div>
      `,
      buttons: [
        {
          text: 'Previous',
          action: () => {
            if (onNavigateToSection) onNavigateToSection('settings');
            setTimeout(() => tour.back(), 500);
          },
          classes: 'btn-secondary'
        },
        {
          text: 'Next',
          action: () => {
            if (onNavigateToSection) onNavigateToSection('clients');
            setTimeout(() => tour.next(), 500);
          },
          classes: 'btn-primary'
        }
      ],
      attachTo: {
        element: 'a[data-section="teams"]',
        on: 'bottom'
      }
    });

    // Client management step
    tour.addStep({
      id: 'clients',
      title: 'Add Your First Client',
      text: `
        <div class="tour-content">
          <div class="tour-icon">👥</div>
          <h3 class="tour-title">Create your client database</h3>
          <p class="tour-description">Start building your client list. You'll need at least one client to create invoices.</p>
          <div class="tour-features">
            <div class="feature-item">✓ Client names & contact information</div>
            <div class="feature-item">✓ Project addresses & details</div>
            <div class="feature-item">✓ Communication history tracking</div>
          </div>
          <div class="tour-progress">
            <div class="progress-bar">
              <div class="progress-fill" style="width: 60%"></div>
            </div>
            <span class="progress-text">Step 4 of ${userPlan === 'pro' ? '10' : '6'}</span>
          </div>
        </div>
      `,
      buttons: [
        {
          text: 'Previous',
          action: () => {
            if (onNavigateToSection) onNavigateToSection('teams');
            setTimeout(() => tour.back(), 500);
          },
          classes: 'btn-secondary'
        },
        {
          text: 'Next',
          action: () => {
            if (onNavigateToSection) onNavigateToSection('invoices');
            setTimeout(() => tour.next(), 500);
          },
          classes: 'btn-primary'
        }
      ],
      attachTo: {
        element: 'a[data-section="clients"]',
        on: 'bottom'
      }
    });

    // Invoice creation step
    tour.addStep({
      id: 'invoices',
      title: 'Create Your First Invoice',
      text: `
        <div class="tour-content">
          <div class="tour-icon">💼</div>
          <h3 class="tour-title">Build professional invoices</h3>
          <p class="tour-description">This is where the magic happens! Create detailed invoices with real-time material pricing.</p>
          <div class="tour-features">
            <div class="feature-item">✓ Select clients for each job</div>
            <div class="feature-item">✓ Add materials with real-time pricing</div>
            <div class="feature-item">✓ Include labour costs from your team</div>
            <div class="feature-item">✓ Add markup, overhead, and tax</div>
            <div class="feature-item">✓ Generate invoice PDFs</div>
            <div class="feature-item">✓ Send by email from the Actions menu (Pro)</div>
          </div>
          <div class="tour-progress">
            <div class="progress-bar">
              <div class="progress-fill" style="width: 80%"></div>
            </div>
            <span class="progress-text">Step 5 of ${userPlan === 'pro' ? '8' : '6'}</span>
          </div>
        </div>
      `,
      buttons: [
        {
          text: 'Previous',
          action: () => {
            if (onNavigateToSection) onNavigateToSection('clients');
            setTimeout(() => tour.back(), 500);
          },
          classes: 'btn-secondary'
        },
        {
          text: 'Next',
          action: () => tour.next(),
          classes: 'btn-primary'
        }
      ],
      attachTo: {
        element: 'a[data-section="invoices"]',
        on: 'bottom'
      }
    });

    // Invoice actions step
    tour.addStep({
      id: 'invoice-actions',
      title: 'Invoice Actions',
      text: `
        <div class="tour-content">
          <div class="tour-icon">🛠️</div>
          <h3 class="tour-title">Send and track invoices</h3>
          <p class="tour-description">Open Actions to build your invoice PDF, set a final price, and email your client on Pro.</p>
          <div class="tour-features">
            <div class="feature-item">✓ Invoice — compose and preview your PDF</div>
            <div class="feature-item">✓ Send the invoice by email (Pro)</div>
            <div class="feature-item">✓ Final price when the job is agreed</div>
            <div class="feature-item">✓ Status tracking in the list</div>
          </div>
          <div class="tour-progress">
            <div class="progress-bar">
              <div class="progress-fill" style="width: ${userPlan === 'pro' ? '60%' : '100%'}"></div>
            </div>
            <span class="progress-text">Step 6 of ${userPlan === 'pro' ? '8' : '6'}</span>
          </div>
        </div>
      `,
      buttons: [
        {
          text: 'Previous',
          action: () => {
            if (onNavigateToSection) onNavigateToSection('invoices');
            setTimeout(() => tour.back(), 500);
          },
          classes: 'btn-secondary'
        },
        {
          text: userPlan === 'pro' ? 'Next' : 'Finish',
          action: () => {
            if (userPlan === 'pro') {
              if (onNavigateToSection) onNavigateToSection('projects');
              setTimeout(() => tour.next(), 500);
            } else {
              tour.complete();
              setIsTourActive(false);
              if (onTourComplete) onTourComplete();
            }
          },
          classes: 'btn-primary'
        }
      ],
      attachTo: {
        element: '.invoices-section',
        on: 'bottom'
      }
    });

    // Pro plan features
    if (userPlan === 'pro') {
      // Project creation
      tour.addStep({
        id: 'projects',
        title: 'Create Projects',
        text: `
          <div class="tour-content">
            <div class="tour-icon">📋</div>
            <h3 class="tour-title">Turn invoices into projects</h3>
            <p class="tour-description">Link accepted work to organized projects with timelines, expenses, and tracking.</p>
            <div class="tour-features">
              <div class="feature-item">✓ Create projects with timelines</div>
              <div class="feature-item">✓ Set start and end dates</div>
              <div class="feature-item">✓ Link projects to invoices and clients</div>
              <div class="feature-item">✓ Track project progress</div>
              <div class="feature-item">✓ Track expenses and receipts per project</div>
            </div>
            <div class="tour-progress">
              <div class="progress-bar">
                <div class="progress-fill" style="width: 75%"></div>
              </div>
              <span class="progress-text">Step 7 of 8</span>
            </div>
          </div>
        `,
        buttons: [
          {
            text: 'Previous',
            action: () => {
              if (onNavigateToSection) onNavigateToSection('invoices');
              setTimeout(() => tour.back(), 500);
            },
            classes: 'btn-secondary'
          },
          {
            text: 'Next',
            action: () => tour.next(),
            classes: 'btn-primary'
          }
        ],
        attachTo: {
          element: 'a[data-section="projects"]',
          on: 'bottom'
        }
      });

      // Expense tracking (last Pro step)
      tour.addStep({
        id: 'expenses',
        title: 'Track Project Expenses',
        text: `
          <div class="tour-content">
            <div class="tour-icon">💰</div>
            <h3 class="tour-title">Monitor project costs and expenses</h3>
            <p class="tour-description">Keep track of project costs with receipt uploads and expense tracking inside each project.</p>
            <div class="tour-features">
              <div class="feature-item">✓ Upload receipt photos</div>
              <div class="feature-item">✓ Track expenses by category</div>
              <div class="feature-item">✓ Monitor project budgets</div>
              <div class="feature-item">✓ See spend against invoice revenue</div>
            </div>
            <div class="tour-progress">
              <div class="progress-bar">
                <div class="progress-fill" style="width: 100%"></div>
              </div>
              <span class="progress-text">Step 8 of 8</span>
            </div>
          </div>
        `,
        buttons: [
          {
            text: 'Previous',
            action: () => {
              if (onNavigateToSection) onNavigateToSection('projects');
              setTimeout(() => tour.back(), 500);
            },
            classes: 'btn-secondary'
          },
          {
            text: 'Finish',
            action: () => {
              tour.complete();
              setIsTourActive(false);
              if (onTourComplete) onTourComplete();
            },
            classes: 'btn-primary'
          }
        ],
        attachTo: {
          element: 'a[data-section="projects"]',
          on: 'bottom'
        }
      });
    }

    // Start the tour
    tour.start();
    setIsTourActive(true);

    // Handle tour completion
    tour.on('complete', () => {
      setIsTourActive(false);
      if (onTourComplete) onTourComplete();
    });

    tour.on('cancel', () => {
      setIsTourActive(false);
      if (onTourComplete) onTourComplete();
    });
  };

  return (
    <div className="product-tour-container">
      {!isTourActive && (
        <button
          onClick={startTour}
          className="tour-trigger-button"
        >
          <svg className="tour-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span>Take Tour</span>
        </button>
      )}
    </div>
  );
};

export default ProductTour; 